/**
 * Save a single TranslationFile to disk immediately, bypassing auto-save debounce.
 * Used when state changes must persist right away (upload, translate start, etc.).
 */

import { Project, TranslationFile } from '../../types';
import { fileSystemPersistence } from './fileSystemPersistence';
import { sanitizeFolderName, sanitizeFileName } from '../../utils/textUtils';

export async function saveFileImmediately(
    rootHandle: FileSystemDirectoryHandle,
    project: Project,
    file: TranslationFile,
): Promise<void> {
    try {
        (fileSystemPersistence as any).handle = rootHandle;
        const category = project.type === 'translate' ? 'translating' : project.type === 'train' ? 'training' : '';
        const projectFolderName = `project_${sanitizeFolderName(project.name, project.id)}`;
        const projectPath = category ? [category, projectFolderName] : [projectFolderName];
        const safeName = sanitizeFileName(file.fileName);
        const shortId = file.id.split('-')[0] || file.id.slice(0, 8);
        const fileBaseName = `${safeName}.${shortId}`;

        // Strip heavy fields before JSON serialization
        const {
            sourceText,
            originalFile,
            docxParagraphMap,
            aiOutput,
            auditResult,
            auditRawOutput,
            semanticMatchResult,
            ...lightState
        } = file as any;

        await fileSystemPersistence.saveJson(
            [...projectPath, 'files'],
            `${fileBaseName}.state.json`,
            lightState,
        );

        // US-19-16: PARTIAL-SAVE PROTECTION (Codex round 1 concern #2).
        // If file was loaded metadata-only (contentLoaded=false), heavy fields are placeholders/empty.
        // Do NOT overwrite disk's existing heavy artifacts — preserve disk truth.
        // Only write heavy artifacts when contentLoaded=true (full content present in memory).
        const canWriteHeavy = file.contentLoaded === true;

        if (canWriteHeavy && sourceText) {
            await fileSystemPersistence.saveFile(
                [...projectPath, 'files'],
                `${fileBaseName}.source.txt`,
                sourceText,
            );
        }

        if (canWriteHeavy && aiOutput && aiOutput.length > 0) {
            await fileSystemPersistence.saveFile(
                [...projectPath, 'files'],
                `${fileBaseName}.translation.txt`,
                aiOutput.join('\n'),
            );
        }

        if (canWriteHeavy && originalFile instanceof ArrayBuffer && originalFile.byteLength > 0) {
            const originalExt = file.originalFormat === 'pptx' ? 'pptx' : 'docx';
            await fileSystemPersistence.saveBinary(
                [...projectPath, 'files'],
                `${fileBaseName}.original.${originalExt}`,
                originalFile,
            );
        }

        if (canWriteHeavy && docxParagraphMap && Array.isArray(docxParagraphMap) && docxParagraphMap.length > 0) {
            await fileSystemPersistence.saveJson(
                [...projectPath, 'files'],
                `${fileBaseName}.paragraphMap.json`,
                docxParagraphMap,
            );
        }

        console.log(`[ImmediateSave] Saved ${file.fileName} (status=${file.translationStatus}, contentLoaded=${canWriteHeavy})`);
    } catch (err) {
        console.error('[ImmediateSave] Failed:', err);
    }
}
